import { useEffect, useMemo, useState } from 'react';
import Icon from '../components/Icon.jsx';
import './AnatomySlide.css';

/*
  V4:
  - marker positions matched to the user's red-dot reference image
  - focus center decoupled from marker position for cleaner zoom targets
  - overview keeps the whole anatomy visible before selection
*/
const anatomyParts = [
  {
    id: 'auricle',
    label: 'Auricle',
    group: 'External Ear',
    x: 4.58,
    y: 6.32,
    focusX: 10.2,
    focusY: 15.8,
    zoom: 1.72,
    labelSide: 'left',
  },
  {
    id: 'canal',
    label: 'External auditory canal',
    group: 'External Ear',
    x: 38.07,
    y: 51.24,
    focusX: 34.8,
    focusY: 48.4,
    zoom: 1.92,
    labelSide: 'below-left',
  },
  {
    id: 'membrane',
    label: 'Tympanic membrane',
    group: 'Middle Ear',
    x: 56.44,
    y: 49.11,
    focusX: 50.6,
    focusY: 47.8,
    zoom: 2.04,
    labelSide: 'above-left',
  },
  {
    id: 'ossicles',
    label: 'Auditory ossicles',
    group: 'Middle Ear',
    x: 58.98,
    y: 32.39,
    focusX: 62.4,
    focusY: 42.0,
    zoom: 2.08,
    labelSide: 'above-left',
  },
  {
    id: 'canals',
    label: 'Semicircular canals',
    group: 'Inner Ear',
    x: 68.43,
    y: 19.22,
    focusX: 71.0,
    focusY: 26.0,
    zoom: 2.02,
    labelSide: 'above-left',
  },
  {
    id: 'cochlea',
    label: 'Cochlea',
    group: 'Inner Ear',
    x: 82.96,
    y: 44.94,
    focusX: 80.8,
    focusY: 44.8,
    zoom: 2.0,
    labelSide: 'left',
  },
  {
    id: 'nerve',
    label: 'Vestibulocochlear nerve (N VIII)',
    group: 'Inner Ear',
    x: 91.48,
    y: 35.03,
    focusX: 86.7,
    focusY: 38.8,
    zoom: 1.94,
    labelSide: 'right',
  },
];

const descriptions = {
  auricle: 'Visible outer ear structure that collects sound and helps direct sound into the ear canal.',
  canal: 'The external auditory canal carries sound waves inward toward the tympanic membrane.',
  membrane: 'The tympanic membrane (eardrum) vibrates in response to incoming sound.',
  ossicles: 'The malleus, incus, and stapes transmit and amplify vibration through the middle ear.',
  cochlea: 'The cochlea converts mechanical vibration into electrical neural signals.',
  canals: 'The semicircular canals are part of the vestibular system and support balance and orientation.',
  nerve: 'The vestibulocochlear nerve carries hearing and balance information from the inner ear to the brain.',
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function getFocusTransform(part) {
  const scale = part.zoom;
  const fx = part.focusX ?? part.x;
  const fy = part.focusY ?? part.y;
  const minTranslate = 100 - 100 * scale;

  const rawX = 50 - fx * scale;
  const rawY = 50 - fy * scale;

  return {
    scale,
    tx: clamp(rawX, minTranslate, 0),
    ty: clamp(rawY, minTranslate, 0),
  };
}

export default function AnatomySlide({ active }) {
  const [selected, setSelected] = useState(0);
  const [overview, setOverview] = useState(true);
  const [guided, setGuided] = useState(false);
  const part = anatomyParts[selected];

  useEffect(() => {
    if (!active || !guided) return undefined;

    const timer = window.setInterval(() => {
      setOverview(false);
      setSelected((value) => (value + 1) % anatomyParts.length);
    }, 2200);

    return () => window.clearInterval(timer);
  }, [active, guided]);

  useEffect(() => {
    if (!active) setGuided(false);
  }, [active]);

  const choose = (index) => {
    setGuided(false);
    setSelected(index);
    setOverview(false);
  };

  const layerStyle = useMemo(() => {
    if (overview) {
      return {
        transform: 'translate3d(0%, 0%, 0) scale(0.94)',
      };
    }

    const focus = getFocusTransform(part);
    return {
      transform: `translate3d(${focus.tx}%, ${focus.ty}%, 0) scale(${focus.scale})`,
    };
  }, [overview, part]);

  return (
    <section className="slide-content anatomy-slide v2-anatomy anatomy-fit-slide">
      <div className="v2-anatomy-heading reveal-stack">
        <div className="anatomy-heading-copy">
          <div className="eyebrow"><Icon name="stethoscope" />Ear Anatomy</div>
          <h2>
            <span>EXTERNAL EAR</span>
            <em>• MIDDLE EAR • INNER EAR</em>
          </h2>
        </div>

        <div className="viewer-actions v2-viewer-actions">
          <button
            className={guided ? 'is-active' : ''}
            onClick={() => setGuided((value) => !value)}
          >
            <Icon name={guided ? 'pause' : 'play'} />
            {guided ? 'Pause guide' : 'Guided tour'}
          </button>

          <button
            onClick={() => {
              setGuided(false);
              setOverview(true);
            }}
          >
            <Icon name="reset" />Overview
          </button>
        </div>
      </div>

      <div className="v2-anatomy-layout anatomy-fit-layout">
        <div className={`v2-anatomy-stage anatomy-fit-stage ${overview ? 'is-overview' : 'is-focused'}`}>
          <div className="v2-anatomy-grid" />

          <div className="anatomy-canvas-wrap">
            <div className="v2-ear-layer anatomy-coordinate-canvas" style={layerStyle}>
              <img
                className="v2-ear-image"
                src="/assets/ear-anatomy.svg"
                alt="Anatomical cross-section of the human ear"
              />

              {anatomyParts.map((item, index) => (
                <button
                  key={item.id}
                  className={`v2-ear-pin anatomy-precise-pin anatomy-label-${item.labelSide} ${index === selected && !overview ? 'is-active' : ''}`}
                  style={{ left: `${item.x}%`, top: `${item.y}%` }}
                  onClick={() => choose(index)}
                  aria-label={`Focus on ${item.label}`}
                >
                  <i />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="v2-anatomy-regions" aria-hidden="true">
            <span>Outer Ear</span>
            <span>Middle Ear</span>
            <span>Inner Ear</span>
          </div>
        </div>

        <aside className="v2-anatomy-panel anatomy-fit-panel scroll-guard">
          <small>{overview ? 'Guided anatomy' : part.group}</small>
          <h3>{overview ? 'Select a structure' : part.label}</h3>
          <p>
            {overview
              ? 'The hotspot anchors are aligned to the approved reference image. Click any structure or run the guided tour to zoom into the selected anatomy part.'
              : descriptions[part.id]}
          </p>

          <div className="v2-anatomy-list">
            {anatomyParts.map((item, index) => (
              <button
                key={item.id}
                className={index === selected && !overview ? 'is-active' : ''}
                onClick={() => choose(index)}
              >
                <b>{String(index + 1).padStart(2, '0')}</b>
                <span>{item.label}</span>
                <i />
              </button>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
